import { useEffect, useMemo, useState } from "react";
import { useUserpilot } from "@platform/hooks/useUserpilot";

import useDialogStore from "@store/useDialogStore";
import usePlatformStore from "@store/usePlatformStore";
import { useCreateDeal } from "./hooks/useCreateDeal";
import { useCreateCompany } from "./hooks/useCreateCompany";
import { useCompanySuggestions } from "./hooks/useCompanySuggestions";
import { useCreateCompanyFromExternal } from "./hooks/useCreateCompanyFromExternal";
import { getCompanyTaggingOption, getDealTaggingOption } from "@utils/tags.util";
import { cn } from "@utils/tailwind.util";
import { Deal } from "@app-types/common.type";
import { DisconnectedError } from "@app-types/errors";
import { Source } from "@enums/common.enum";
import { RelationObjectType } from "@enums/relations.enum";
import { ICompanyTag, IDealTag } from "@interfaces/tag.interface";
import { ICompany, ISuggestionCompany } from "@interfaces/company.interface";
import { CreateCompanyRequest, CreateDealRequest } from "@app-types/requests.type";
import { DEFAULT_LIMIT_TAGS } from "@components/Sections/TagsSection/hooks/useSearchTags";
import { CompanyAvatar, CompanyAvatarSizes } from "@ui/Avatars";
import { NewEntityOptionItem } from "./NewEntityOptionItem";
import { AddNewCompanyDialog } from "./Company";
import { AddNewDealDialog } from "./Deal";
import { View } from "@ui/View";

const FIRST_FIVE_ITEMS = 5;

interface Props {
  contentType: RelationObjectType;
  searchQuery: string;
  clearQuery: VoidFunction;
  onSelect: (item: ICompanyTag | IDealTag) => void;
  existingCompanies: ICompanyTag[];
  uppercaseLabels?: boolean;
}

export function NewEntityOptions({
  contentType,
  searchQuery,
  clearQuery,
  onSelect,
  existingCompanies,
  uppercaseLabels,
}: Props) {
  const { track } = useUserpilot();
  const { isOutlook } = usePlatformStore();
  const { openDialog, closeDialog } = useDialogStore();
  const { mutateAsync: createCompanyMutate } = useCreateCompany(contentType, closeDialog);
  const { mutateAsync: createCompanyFromExternalMutate, isPending: isCreatingCompany } =
    useCreateCompanyFromExternal(contentType);
  const { mutateAsync: createDealMutate } = useCreateDeal(contentType, closeDialog);
  const { data: fetchedCompanySuggestions } = useCompanySuggestions(searchQuery);

  const [visibleCount, setVisibleCount] = useState(DEFAULT_LIMIT_TAGS);

  const sourceItem = contentType === RelationObjectType.EMAIL_THREAD
    ? "emails"
    : "meetings";
  
  const sourcePlatform = isOutlook()
    ? Source.OUTLOOK_ADDIN
    : Source.CHROME_EXTENSION;

  const companySuggestions = useMemo(() => {
    if (!fetchedCompanySuggestions) return [];

    const existingWebsites = existingCompanies?.map((company) => company.website) ?? [];

    return fetchedCompanySuggestions.filter(
      (suggestion) => !existingWebsites.includes(suggestion.website ?? "")
    );
  }, [existingCompanies, fetchedCompanySuggestions, visibleCount]);

  const handleCreateCompany = async (data: CreateCompanyRequest): Promise<ICompany> => {
    const response = await createCompanyMutate(data);

    if (!!response) {
      const companyTaggingOption = getCompanyTaggingOption(response);
      onSelect(companyTaggingOption);
      clearQuery();
    }

    return response;
  };

  const handleCreateCompanyFromExternal = async (data: ISuggestionCompany) => {
    if (isCreatingCompany) return;
    try {
      const body = {
        external_company: data,
        source: sourcePlatform,
        with_deal: false,
      };

      const response = await createCompanyFromExternalMutate(body);

      if (!!response?.company) {
        const companyTaggingOption = getCompanyTaggingOption(response.company);
        onSelect(companyTaggingOption);
        clearQuery();
      }
    } catch (error) {
      if (!(error instanceof DisconnectedError)) throw error;
    }
  };

  const handleCreateDeal = async (data: CreateDealRequest): Promise<Deal> => {
    const response = await createDealMutate(data);

    if (!!response) {
      const dealTaggingOption = getDealTaggingOption(response);
      onSelect(dealTaggingOption);
      clearQuery();
    }

    return response;
  };

  const handleOpenNewCompanyDialog = () => {
    openDialog(
      <AddNewCompanyDialog
        query={searchQuery}
        onSave={handleCreateCompany}
        sourceItem={sourceItem}
        sourcePlatform={sourcePlatform}
      />,
      { disableOutsideClick: true }
    );
    track(`Click: Open new company dialog (${sourceItem})`);
  };

  const handleOpenNewDealDialog = () => {
    openDialog(
      <AddNewDealDialog
        query={searchQuery}
        onSave={handleCreateDeal}
        sourceItem={sourceItem}
        sourcePlatform={sourcePlatform}
      />,
      { disableOutsideClick: true }
    );
    track(`Click: Open new deal dialog (${sourceItem})`);
  };

  useEffect(() => {
    setVisibleCount(searchQuery.length > 0 ? FIRST_FIVE_ITEMS : DEFAULT_LIMIT_TAGS);
  }, [searchQuery]);

  const isShowMoreSuggestedCompanies = companySuggestions.length > visibleCount;

  return (
    <>
      <View.Condition if={companySuggestions.length > 0}>
        <div className="px-1 pb-3 pt-3 border-b dark:border-gray-600">
          <div
            className={cn(
              "px-2 pb-1.5 text-xs font-medium text-content-secondary dark:text-white",
              { uppercase: uppercaseLabels }
            )}
          >
            Suggestions from global database
          </div>

          <ul className="space-y-1">
            {companySuggestions.slice(0, visibleCount).map((company) => (
              <li
                key={`${company.website}_${company.name}`}
                className="flex cursor-pointer items-center gap-2 hover:bg-background-secondary dark:hover:bg-gray-700 p-2 transition duration-150 ease-in-out rounded-md"
                onClick={() => handleCreateCompanyFromExternal(company)}
                onMouseDown={(e) => e.preventDefault()} // Stop native scrolling
              >
                <CompanyAvatar
                  size={CompanyAvatarSizes.BIG}
                  classNames="!rounded-[5px] w-5 h-5 border border-gray-200 bg-background-clear flex-shrink-0"
                  logoUrl={company.logo || company.logo_url}
                />
                <div className="min-w-0 flex flex-1 items-center gap-1">
                  <div className="max-w-[280px] overflow-hidden text-ellipsis whitespace-nowrap text-sm text-gray-950 dark:text-invertedPrimary80">
                    {company.name}
                  </div>
                  {company.website && (
                    <a
                      href={company.website}
                      onMouseDown={(e) => e.preventDefault()} // Stop native scrolling
                      onClick={(e) => {
                        e.preventDefault(); // Stop opening in the same tab
                        e.stopPropagation(); // Stop creatig new company
                        window.open(company.website, "_blank");
                      }}
                      className="overflow-hidden text-ellipsis whitespace-nowrap text-xs text-gray-500 no-underline transition duration-100 ease-out hover:text-teal-800 dark:hover:text-teal-500 hover:underline"
                    >
                      {company.website.replace(/^(https?:\/\/)?(www\.)?/, "").replace(/\/$/, "")}
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <View.Condition if={isShowMoreSuggestedCompanies}>
            <div
              className="w-fit cursor-pointer mx-1 p-1 text-sm font-medium text-gray-450 transition-all ease-linear hover:text-content-primary dark:text-invertedPrimary80 dark:hover:text-gray-500"
              onClick={() => setVisibleCount(companySuggestions.length)}
              onMouseDown={(e) => e.preventDefault()} // Stop native scrolling
            >
              Show {companySuggestions.length - visibleCount} More
            </div>
          </View.Condition>
        </div>
      </View.Condition>

      <div className="px-1 pb-3 pt-3">
        <div
          className={cn("px-2 pb-1.5 text-xs font-medium text-content-secondary dark:text-white", {
            uppercase: uppercaseLabels,
          })}
        >
          Add new
        </div>

        <ul className="space-y-1">
          <NewEntityOptionItem
            searchQuery={searchQuery}
            title="Company"
            action={handleOpenNewCompanyDialog}
          />
          <NewEntityOptionItem
            searchQuery={searchQuery}
            title="Deal"
            action={handleOpenNewDealDialog}
          />
        </ul>
      </div>
    </>
  );
}
